import { useTranslation } from '@repo/i18n'
import { SlidersHorizontal, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge } from '#components/badge'
import { Button } from '#components/button'
import {
	Popover,
	PopoverContent,
	PopoverTitle,
	PopoverTrigger
} from '#components/popover'

export function FiltersBar(props: {
	search?: ReactNode
	children: ReactNode
	actions?: ReactNode
	active: { key: string; label: string; onRemove: () => void }[]
	onReset: () => void
}) {
	const { t } = useTranslation()
	return (
		<div
			className='flex w-full min-w-0 flex-wrap items-center gap-2'
			data-slot='filters-bar'
		>
			{props.search}
			<Popover>
				<PopoverTrigger
					aria-label={t('shared:filters.title')}
					render={<Button size='sm' variant='outline' />}
				>
					<SlidersHorizontal aria-hidden='true' />
					{t('shared:filters.title')}
					{props.active.length > 0 && (
						<Badge variant='secondary'>{props.active.length}</Badge>
					)}
				</PopoverTrigger>
				<PopoverContent
					align='start'
					className='max-h-(--available-height) w-80 max-w-[calc(100vw-2rem)] overflow-y-auto'
					data-slot='filters-panel'
				>
					<PopoverTitle>{t('shared:filters.title')}</PopoverTitle>
					{props.children}
					<Button
						disabled={props.active.length === 0}
						onClick={props.onReset}
						size='sm'
						variant='outline'
					>
						{t('shared:filters.reset')}
					</Button>
				</PopoverContent>
			</Popover>
			{props.active.length > 0 && (
				<fieldset
					aria-label={t('shared:filters.active')}
					className='contents'
					data-slot='active-filters'
				>
					{props.active.map((filter) => (
						<Button
							aria-label={t('shared:filters.remove', {
								label: filter.label
							})}
							className='h-auto min-h-7 max-w-full whitespace-normal text-left font-normal text-xs'
							key={filter.key}
							onClick={filter.onRemove}
							size='sm'
							variant='outline'
						>
							<span className='min-w-0 break-words'>
								{filter.label}
							</span>
							<X aria-hidden='true' className='size-3' />
						</Button>
					))}
				</fieldset>
			)}
			{props.actions && (
				<div className='ml-auto flex flex-wrap items-center gap-2'>
					{props.actions}
				</div>
			)}
		</div>
	)
}

export function FilterGroup(props: { label: string; children: ReactNode }) {
	return (
		<fieldset className='min-w-0'>
			<legend className='mb-2 font-medium text-muted-foreground text-xs'>
				{props.label}
			</legend>
			<div className='flex min-w-0 flex-wrap gap-2'>{props.children}</div>
		</fieldset>
	)
}
